/* Enterprise F&O API client. Talks to the F&O gateway (YARP, port 5200) and nothing else.
   Config (all optional):
     VITE_FO_GATEWAY_URL   gateway base URL, default https://fo.cilpron.com
     VITE_FO_TOKEN_KEY     sessionStorage/localStorage key holding the Cilpron JWT, default "fo_access_token"
     VITE_FO_COMPANY_ID    company id, default the demo company */

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
export const BASE = (env.VITE_FO_GATEWAY_URL || "https://fo.cilpron.com").replace(/\/+$/, "");
const TOKEN_KEYS = [env.VITE_FO_TOKEN_KEY, "fo_access_token", "access_token", "hcm_access_token", "token"].filter((k): k is string => !!k);
export const COMPANY = env.VITE_FO_COMPANY_ID || "11111111-1111-1111-1111-111111111111";

export const getToken = (): string => {
  for (const store of [sessionStorage, localStorage]) {
    for (const k of TOKEN_KEYS) {
      try {
        const v = store.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
      } catch {
        /* storage blocked */
      }
    }
  }
  return "";
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

export type Rec = { id: string } & Record<string, unknown>;
export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const problem = (status: number, body: unknown): string => {
  const b = body as { title?: string; detail?: string; errors?: Record<string, string[]> } | null;
  if (b?.errors) {
    const first = Object.entries(b.errors)[0];
    if (first) return `${first[0]}: ${first[1][0]}`;
  }
  return b?.detail || b?.title || (status === 401 ? "You are not signed in." : status === 403 ? "You do not have permission for this." : `Request failed (${status})`);
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-Company-Id": COMPANY,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, `Cannot reach the F&O gateway at ${BASE}.`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = text;
  }
  if (!res.ok) throw new ApiError(res.status, problem(res.status, json), json);
  return json as T;
}

export const api = {
  get: <T = Rec>(path: string) => call<T>("GET", path),
  post: <T = Rec>(path: string, body?: unknown) => call<T>("POST", path, body ?? {}),
  put: <T = Rec>(path: string, body: unknown) => call<T>("PUT", path, body),
  del: (path: string) => call<void>("DELETE", path),
  /** one page of a resource */
  page: <T = Rec>(path: string, o: { page?: number; pageSize?: number; q?: string; sort?: string; f?: Record<string, string> } = {}) => {
    const p = new URLSearchParams();
    p.set("page", String(o.page ?? 1));
    p.set("pageSize", String(o.pageSize ?? 50));
    if (o.q) p.set("q", o.q);
    if (o.sort) p.set("sort", o.sort);
    for (const [k, v] of Object.entries(o.f ?? {})) p.set(`f.${k}`, v);
    return call<Page<T>>("GET", `${path}?${p}`);
  },
  /** every row of a (small) resource, following the pages */
  all: async <T = Rec>(path: string, f?: Record<string, string>): Promise<T[]> => {
    const out: T[] = [];
    for (let page = 1; page <= 40; page++) {
      const r = await api.page<T>(path, { page, pageSize: 200, f });
      out.push(...r.items);
      if (page >= r.totalPages) break;
    }
    return out;
  },
};

export const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));